import { Formik } from "formik";
import * as Yup from "yup";
import CustomText from "../atomic/CustomText";
import FieldRenderer from "./forms/FieldRenderer";
import { forwardRef, Fragment, useCallback, useEffect, useState } from "react";
import ProgressBar from "./ProgressBar";
import MultiStepFormProps from "../../services/models/formModels/MultiStepForm/multiStepFormProps.model";
import StepItemProps from "../../services/models/formModels/MultiStepForm/stepItemProps.model";
import SectionFieldsProps from "../../services/models/formModels/MultiStepForm/sectionFieldProps.model";
import CustomTextInputProps from "../../services/models/customTextInputProps.mode";
import CustomRadioInputProps from "../../services/models/customRadioInput.model";
import CustomCheckBoxInputProps from "../../services/models/customCheckBoxInput.model";
import ArrayInputProps from "../../services/models/formModels/MultiStepForm/arrayInputProps.model";
import AdminLayout from "../../layouts/AdminLayout";
import utils from "../../services/utils";
import DetailRenderer from "./forms/DetailRenderer";
import AdminContentLayout from "../../layouts/AdminContentLayout";
import ErrorCross from "../../assets/svg/ErrorCross";
import FormTabCheck from "../../assets/svg/FormTabCheck";
import OnlineOrderValuesModel from "../../services/models/formModels/onlineOrderValues.model";
import ButtonProps from "../../services/models/buttonProps.model";
import useDeleteOrder from "../../services/utils/custom hooks/OnlineOrder/useDeleteOrder";
import useMobile from "../../utils/helpers/useMobile";
import DeleteOrder from "../../assets/svg/DeleteOrder";
import MobileStepsDropdown from "../molecular/OnlineOrderDropdownMobile";
import { isStepSectionTouched } from "../../utils/helpers/stepTouchedHelpers";
import InformationMissingError from "../../assets/svg/InformationMissingError";
import SccIntegratedTestBanner from "./forms/SccIntegratedTestBanner";

const SSN_ERROR_PATH = ["patientInfo", "securityInfo", "ssn"];

const hasErrorsOtherThanSSN = (node: unknown, path: string[] = []): boolean => {
  if (node === null || node === undefined) {
    return false;
  }

  if (typeof node !== "object") {
    const isSsnPath =
      path.length === SSN_ERROR_PATH.length &&
      path.every((segment, index) => SSN_ERROR_PATH[index] === segment);
    const hasValue =
      typeof node === "string" ? node.trim().length > 0 : Boolean(node);

    return hasValue ? !isSsnPath : false;
  }

  if (Array.isArray(node)) {
    return node.some((child, index) =>
      hasErrorsOtherThanSSN(child, [...path, index.toString()])
    );
  }

  return Object.keys(node as Record<string, unknown>).some((key) =>
    hasErrorsOtherThanSSN((node as Record<string, unknown>)[key], [
      ...path,
      key,
    ])
  );
};
interface SectionProps extends SectionFieldsProps {
  readonly sectionClass?: string;
  readonly mode?: string;
  readonly isUpdate?: boolean;
  readonly validateForm?: any;
}

function SectionFields({
  fieldsContainerClass,
  step,
  touched,
  errors,
  values,
  groupFields,
  getFieldProps,
  setFieldValue,
  setFieldTouched,
  getFieldMeta,
}: SectionFieldsProps): JSX.Element {
  return (
    <div
      className={
        step.displayWhen?.hasOwnProperty("isAdditionalClinician")
          ? ""
          : fieldsContainerClass
      }
    >
      {step?.fieldContainerHeading && (
        <CustomText
          fontWeight="font-semibold"
          text={step?.fieldContainerHeading}
          className="my-2"
          fontSize="font-xl"
        />
      )}
      <FieldRenderer
        fields={step.fields.map((field: any, parentIndex: any) => {
          if ((field as ArrayInputProps)?.arrayName) {
            return {
              ...field,
              values,
              setFieldValue,
              currIndex: parentIndex,
              totalFields: step.fields.length,
              fields: (field as ArrayInputProps)?.fields?.map(
                (nestedField, index) => ({
                  ...nestedField,
                  errors,
                  touched,
                  setFieldTouched,
                  setFieldValue,
                  values,

                  parentContainerClass: `${nestedField?.parentContainerClass} ${groupFields?.includes?.(
                    nestedField.labelInfo?.inputId ?? ""
                  )
                      ? "md:w-[46%] inline-block"
                      : ""
                    }`,
                  getFieldMeta,
                  ...getFieldProps(
                    `${(field as ArrayInputProps).arrayName}.${parentIndex}.${nestedField.labelInfo?.inputId
                    }`
                  ),
                })
              ),
            };
          }

          return {
            ...field,
            errors,
            touched,
            setFieldValue,
            values,
            setFieldTouched,
            parentContainerClass: `${(field as CustomTextInputProps)?.parentContainerClass
              } ${groupFields?.includes?.(
                (
                  field as
                  | CustomCheckBoxInputProps
                  | CustomTextInputProps
                  | CustomRadioInputProps
                ).labelInfo?.inputId ?? ""
              )
                ? "md:!w-[47%] align-middle !inline-block"
                : ""
              }`,
            getFieldMeta,
            ...getFieldProps(
              (
                field as
                | CustomCheckBoxInputProps
                | CustomTextInputProps
                | CustomRadioInputProps
              )?.labelInfo?.inputId ?? ""
            ),
          };
        })}
      />
    </div>
  );
}

function StepItem({
  step,
  touched,
  values,
  isValid,
  setValidSteps,
  onInvalidStep,
}: Readonly<StepItemProps>): JSX.Element | null {
  const [hasErrors, setHasErrors] = useState(false);
  const [sectionTouched, setSectionTouched] = useState(false);

  useEffect(() => {
    const isTouched = isStepSectionTouched(step, touched);
    setSectionTouched(isTouched);

    step.stepValidation
      .validate(values, { context: values })
      .then(() => {
        setHasErrors(false);
        setValidSteps((prevState: string[]) => {
          if (!prevState.includes(step.stepTitle) && !!step.stepTitle) {
            prevState.push(step.stepTitle);
          }
          return prevState;
        });
        if (onInvalidStep && step.stepTitle) {
          onInvalidStep(step.stepTitle, false);
        }
      })
      .catch(() => {
        setHasErrors(true);
        setValidSteps((prevState: string[]) => {
          if (prevState.includes(step.stepTitle)) {
            return prevState.filter((title) => title !== step.stepTitle);
          }
          return prevState;
        });
        if (onInvalidStep && step.stepTitle) {
          onInvalidStep(step.stepTitle, isTouched);
        }
      });
  }, [touched, values, onInvalidStep, step, setValidSteps]);

  if (!!step.stepTitle === false) return null;
  return (
    <a
      href={`#${step.stepTitle}`}
      className={`inline-block mb-3 text-royalBlue hover:!text-activeLink ${hasErrors && sectionTouched
          ? "!text-[#f00]"
          : (!hasErrors && sectionTouched) || isValid
            ? "!text-primary"
            : ""
        }`}
    >
      <div className="w-full flex items-center">
        {hasErrors && sectionTouched ? (
          <ErrorCross
            height={15.5}
            width={15.5}
            className="mr-1 w-8 md:w-auto"
            fill="red"
          />
        ) : (
          <FormTabCheck
            className="mr-1 !text-xl w-8 md:w-auto"
            fill={
              (!hasErrors && sectionTouched) || isValid ? "#18B5B7" : "none"
            }
            isvalid={
              (!hasErrors && sectionTouched) || isValid ? "white" : "#d0d4db"
            }
            stroke={
              (!hasErrors && sectionTouched) || isValid ? "#18B5B7" : "#d0d4db"
            }
          />
        )}
        <CustomText
          text={step.stepTitle}
          fontWeight={
            (!hasErrors && sectionTouched) || isValid
              ? "font-semibold"
              : "font-normal"
          }
          fontSize="text-xs md:text-sm"
          className="hover:font-semibold"
        />
      </div>
    </a>
  );
}

function Section({
  step,
  errors,
  touched,
  values,
  getFieldProps,
  groupFields,
  getFieldMeta,
  fieldsContainerClass,
  setFieldValue,
  setFieldError,
  fieldsContainerHeading,
  setFieldTouched,
  validateForm,
  mode,
  isUpdate,
}: SectionProps): JSX.Element | null {
  if (!!step.displayWhen) {
    if (!utils.displayHandler(step.displayWhen, values)) return null;
  }
  const sectionKeysAndValues: any = {};
  let groupRendererFields: string[] = [];

  step.fields.forEach((field: any) => {
    const key: any = (field as CustomTextInputProps).labelInfo?.inputId ?? "";
    const label: any = (field as CustomTextInputProps).labelInfo?.label ?? "";

    let shouldDisplay = true;

    if (
      (field?.labelInfo?.inputId == "isAdditionalClinician" ||
        field?.labelInfo?.inputId === "isAttestation") &&
      isUpdate
    ) {
      field.disabled = true;
    }
    if (!!field.displayWhen) {
      shouldDisplay = utils.displayHandler(field.displayWhen, values);
    }
    if (shouldDisplay) sectionKeysAndValues[label] = getFieldMeta(key).value;

    if (groupFields?.includes(key)) {
      groupRendererFields.push(label);
    }
  });

  const subStepDescription =
    step.stepTitle === "Supporting Documents" && values?.testTypeId === 1
      ? "Note: Pathology reports and insurance information will be required to process the order. Provide the endoscopy report if available."
      : step.subStepDescription;
  return (
    <div
      id={`${step.stepTitle}`}
      key={step.stepTitle}
      className={step.sectionClass}
    >
      {mode !== "review" && (
        <Fragment>
          <CustomText
            fontWeight="font-semibold"
            text={step.stepTitle}
            className={`my-2 ${mode === "review" ? "uppercase tracking-[0.18em] mb-6" : ""
              } reviewCardHeading`}
            fontSize="text-sm"
          />
          {!!step.stepDescription && (
            <CustomText
              fontWeight="font-normal"
              text={step.stepDescription}
              className="my-2"
            />
          )}
          {!!step.subStepDescription && (
            <CustomText
              fontWeight="font-medium"
              text={subStepDescription}
              className="my-2"
            />
          )}
        </Fragment>
      )}

      {step?.customBanner && step.customBanner({ setFieldValue, values })}
      {step.customStep && mode !== "review" ? (
        step.customStep({
          ...step,
          errors,
          touched,
          values,
          setFieldError,
          getFieldProps,
          setFieldValue,
          setFieldTouched,
          validateForm,
        })
      ) : (
        <Fragment>
          {mode === "review" ? (
            <Fragment>
              {step.customReview ? (
                <Fragment>
                  <step.customReview
                    values={values}
                    setFieldValue={setFieldValue}
                  />
                </Fragment>
              ) : (
                <Fragment>
                  {step.hideReview ? null : (
                    <Fragment>
                      <CustomText
                        fontWeight="font-semibold"
                        text={
                          !!step.stepTitle
                            ? step.stepTitle
                            : step.fieldContainerHeading
                        }
                        className={`my-2 ${mode === "review"
                            ? "uppercase tracking-[0.18em] mb-6"
                            : ""
                          }`}
                        fontSize="font-xl"
                      />
                      <div className="bg-sideNavGray px-6 py-4 border border-roundedInputBorder rounded">
                        <CustomText
                          fontWeight={
                            mode === "review" ? "font-medium" : "font-semibold"
                          }
                          text={
                            !!step.stepTitle
                              ? step.stepTitle
                              : step.fieldContainerHeading
                          }
                          className={`my-2 `}
                        // fontSize="font-xl"
                        />
                        <DetailRenderer
                          data={sectionKeysAndValues}
                          groupFields={groupRendererFields ?? []}
                          hiddenFields={[]}
                          className=""
                        />
                      </div>
                    </Fragment>
                  )}
                </Fragment>
              )}
            </Fragment>
          ) : (
            <SectionFields
              errors={errors}
              values={values}
              getFieldMeta={getFieldMeta}
              touched={touched}
              setFieldValue={setFieldValue}
              getFieldProps={getFieldProps}
              groupFields={groupFields}
              setFieldTouched={setFieldTouched}
              step={step}
              fieldsContainerClass={`${fieldsContainerClass} ${step.stepTitle === "Supporting Documents" ? "flex flex-col" : ""
                }`}
            />
          )}
        </Fragment>
      )}
    </div>
  );
}

const ErrorSummary = ({ invalidSteps }: { invalidSteps: string[] }) => {
  if (invalidSteps.length === 0) return null;

  // Format the steps list and remove "Information" from all but the last item
  const formatStepsList = (steps: string[]) => {
    if (steps.length === 1) {
      return steps[0];
    }

    // Process each step to remove "Information" from all but the last one
    const processedSteps = steps.map((step, index) => {
      if (index === steps.length - 1) {
        return step;
      } else {
        return step.replace(" Information", "");
      }
    });

    if (processedSteps.length === 2) {
      return `${processedSteps[0]} and ${processedSteps[1]}`;
    } else {
      const allButLast = processedSteps.slice(0, -1).join(", ");
      const lastItem = processedSteps[processedSteps.length - 1];
      return `${allButLast}, and ${lastItem}`;
    }
  };

  return (
    <div className="sticky z-50 py-5 px-4 top-0">
      <div className="flex items-center">
        <InformationMissingError className="w-6 h-5 mr-2" />
        <p className="font-medium text-xs text-[#D92D20]">
          Information missing in {formatStepsList(invalidSteps)}
        </p>
      </div>
    </div>
  );
};
function MultiStepFrom(props: MultiStepFormProps, ref: any): JSX.Element {
  const {
    showProgressBar,
    steps,
    stepContainerClassName,
    formContainerClassName,
    sectionContainerClassName,
    fieldsContainerClass,
    globalSectionClassName,
    initialValues,
    groupFields,
    onSubmit,
    isUpdate,
    shouldValidate,
    submitting,
    customSubmissionFunction,
    initialTouchedValues,
  } = props;
  const [validationSchema, setValidationSchema] = useState<Yup.AnyObject>(
    Yup.object().shape({})
  );
  const [initialTouched, setInitialTouched] = useState({});
  const [mode, setMode] = useState<string>("edit");
  const [saving, setSaving] = useState<boolean>(false);
  const [validSteps, setValidSteps] = useState<string[]>([]);
  const isMobile = useMobile();
  const [currentStep, setCurrentStep] = useState<any>(() => {
    return steps.length > 0 ? steps[0].stepTitle : null;
  });

  function toggleMode() {
    setMode((prevState) => (prevState === "edit" ? "review" : "edit"));
  }

  useEffect(() => {
    let initalValuesKey: any = initialValues;
    let touched: any = {};

    steps.forEach((step) => {
      setValidationSchema((prevState) =>
        prevState?.concat?.(step.stepValidation)
      );
    });

    if (isUpdate || shouldValidate) {
      Object.keys(initialValues).forEach((key: string) => {
        if (!!initalValuesKey[key]) {
          touched[key] = true;
        }

        if (isUpdate) {
          if (Number((initialValues as OnlineOrderValuesModel).testTypeId) === 2) {
            touched.historyPhysicalExam = {
              isPatientImmunocompromised: true,
              isTumorLocatedOnHeadOrNeck: true,
              tumorDiameter: true,
              additionalHistoryPhysicalExam: true,
            };
            touched.pathologySurgicalFindings = {
              isPerineuralInvolvement: true,
              isPoorlyDifferentiated: true,
              additionalPathologySurgicalFindings: true,
            };
          }
          if ((initialValues as OnlineOrderValuesModel).testTypeId === 4) {
            touched.isExposedToRadiation = true;
          }
          if ((initialValues as OnlineOrderValuesModel).testTypeId === 3) {
            touched.isSentinelLymphNodeBiopsy = true;
          }
          if ((initialValues as OnlineOrderValuesModel).testTypeId === 5) {
            touched.isPrimaryCutaneousMelanocyticNeoplasm = true;
          }
          if ((initialValues as OnlineOrderValuesModel).testTypeId === 9) {
            touched.isConsideredForTherapy = true;
          }
        }

        if (
          typeof initalValuesKey[key] === "object" &&
          !Array.isArray(initalValuesKey[key]) &&
          initalValuesKey[key] !== null &&
          initalValuesKey[key] !== undefined
        ) {
          Object.keys(initalValuesKey[key]).forEach((nestedKey: string) => {
            if (isUpdate) {
              touched[key] = {
                ...touched[key],
                [nestedKey]: true,
              };
            } else if (!!initalValuesKey[key][nestedKey] && !isUpdate) {
              touched[key] = {
                ...touched[key],
                [nestedKey]: true,
              };
            }
          });
        }
      });
      setInitialTouched(touched);
    }
  }, []);
  const [invalidSteps, setInvalidSteps] = useState<string[]>([]);

  const handleInvalidStep = useCallback(
    (stepName: string, hasError: boolean) => {
      setInvalidSteps((prev) => {
        if (hasError) {
          return prev.includes(stepName) ? prev : [...prev, stepName];
        } else {
          return prev.filter((step) => step !== stepName);
        }
      });
    },
    []
  );
  useEffect(() => {
    ref?.current
      .validateForm(initialValues)
      .then((res: any) => { })
      .catch((error: any) => console.log({ error }));
  }, [initialTouched]);

  // Reset saving state when submitting changes from true to false
  useEffect(() => {
    if (!submitting && saving) {
      setSaving(false);
    }
  }, [submitting, saving]);

  return (
    <Formik
      enableReinitialize
      innerRef={ref}
      initialValues={initialValues}
      initialTouched={
        isUpdate
          ? { ...initialTouchedValues, ...initialTouched }
          : { ...initialTouchedValues }
      }
      validationSchema={validationSchema}
      isInitialValid={isUpdate || shouldValidate ? undefined : false}
      onSubmit={onSubmit}
      validateOnMount={isUpdate || shouldValidate ? true : false}
      validateOnChange={true}
      validateOnBlur={true}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        handleSubmit,
        validateForm,
        isSubmitting,
        getFieldMeta,
        isValid,
        setFieldError,
        setFieldValue,
        setFieldTouched,
        getFieldProps,
        /* and other goodies */
      }) => {
        const pageTitle = isUpdate
          ? "Update Online Order | CSTL"
          : "Online Order | CSTL";
        const subHeaderTitle = isUpdate
          ? "Update Online Order"
          : "Online Order";

        const progressPercentage = Math.round(
          (validSteps.filter((step) => {
            if ((values as OnlineOrderValuesModel).testTypeId === 8) {
              return (
                ["Clinical Information", "Laboratory Information"].includes(
                  step
                ) === false
              );
            }
            return step;
          }).length /
            steps.filter((step) => {
              if (!!step.displayWhen) {
                return utils.displayHandler(step.displayWhen, values);
              }
              return step.fieldContainerHeading !== "Patient Information";
            }).length) *
          100
        );

        const testTypeId = Number(
          (values as OnlineOrderValuesModel).testTypeId
        );
        const ssnError =
          (errors as any)?.patientInfo?.securityInfo?.ssn ?? null;
        const missingSSNOnly =
          (testTypeId === 4 || testTypeId === 7) &&
          (typeof ssnError === "string"
            ? ssnError.trim().length > 0
            : Boolean(ssnError)) &&
          !hasErrorsOtherThanSSN(errors);

        const submitDisabled =
          submitting || (!isValid && !missingSSNOnly) || saving;

        const customButtons: ButtonProps[] = [
          {
            buttonTheme: isMobile ? "transparent" : "secondary",
            key: "delete",
            textInformation: {
              text: isMobile ? (
                <DeleteOrder className="mt-1.5" />
              ) : (
                "Delete order"
              ),
              fontWeight: isMobile ? "font-medium" : "font-semibold",
              fontSize: "xs:text-xs md:text-xs",
            },
            onClick: props.handleDeleteOrder,
            type: "button",
            className: "mr-3 md:mr-6 ",
            disabled: (initialValues as any)?.status >= 40,
          },
          {
            buttonTheme: isMobile ? "transparent" : "secondary",
            textInformation: {
              text: mode === "edit" ? "Review" : "Edit",
              fontWeight: isMobile ? "font-medium" : "font-semibold",
              fontSize: "xs:text-xs md:text-xs",
            },
            onClick: toggleMode,
            type: "button",
            className: "mr-3 md:mr-6",
          },
          {
            buttonTheme: isMobile ? "transparent" : "secondary",
            textInformation: {
              text: isMobile ? "Save & Exit" : "Save and Exit",
              fontWeight: isMobile ? "font-medium" : "font-semibold",
              fontSize: "xs:text-xs md:text-xs",
            },
            onClick: () => {
              setSaving(true);
              onSubmit({ ...values, isSave: "true" });
            },
            type: "button",
            className: "mr-3 md:mr-6",
            disabled:
              !!(values as OnlineOrderValuesModel)?.testTypeId === false ||
              (submitting && saving) /* ||
              (values as OnlineOrderValuesModel)?.notes?.length > 500 */,
            loading: saving && submitting,
          },
          {
            buttonTheme: isMobile ? "transparent" : "primary",
            textInformation: {
              text: "Submit",
              fontWeight: isMobile ? "font-medium" : "font-semibold",
              fontSize: "xs:text-xs md:text-xs",
              fontColor:
                submitting || (!isValid && !missingSSNOnly) || saving
                  ? "text-textDisabledGray"
                  : "",
            },
            // onClick: onSubmit,
            onClick: customSubmissionFunction
              ? () => customSubmissionFunction(values)
              : () => handleSubmit(),
            type: "submit",
            className: "mr-3 md:mr-6",
            disabled: submitDisabled,
            loading: submitting && !saving,
            tooltipProps: {
              required: true,
              tooltipText:
                !isValid && !missingSSNOnly
                  ? "Please complete the order before submitting"
                  : "",
              id: "Online Order",
              className: "!max-w-[500px]",
            },
          },
        ];

        
        return (
          <AdminContentLayout
            pageTitle={pageTitle}
            subHeaderTitle={subHeaderTitle}
            subHeaderContainerClassName="max-h-[57px] items-center"
            showButtonInSubheaderForMobile
            hideSubHeaderSelectors
            customButtons={customButtons.filter((button) => {
              if (isUpdate && mode === "review") return true;
              return (
                button.textInformation.text !== "Delete order" &&
                button?.key !== "delete"
              );
            })}
          >
            <div className={formContainerClassName}>
              {/* Desktop Sidebar */}
              <div className={stepContainerClassName + " hidden md:block"}>
                {steps.map((step) => {
                  if (!!step.displayWhen) {
                    if (!utils.displayHandler(step.displayWhen, values)) {
                      return null;
                    }
                  }
                  return (
                    <StepItem
                      key={step.stepTitle}
                      setValidSteps={setValidSteps}
                      onInvalidStep={handleInvalidStep}
                      {...{ step, errors, touched, values, isValid }}
                    />
                  );
                })}
              </div>

              {invalidSteps.length > 0 && (
                <div className="block md:hidden">
                  <ErrorSummary invalidSteps={invalidSteps} />
                </div>
              )}
              {/* Mobile Dropdown */}
              <div className="block md:hidden">
                <MobileStepsDropdown
                  steps={steps}
                  values={values}
                  current={currentStep}
                  onSelect={(sectionKey) => setCurrentStep(sectionKey)}
                />
              </div>

              <div className={sectionContainerClassName}>
                {showProgressBar && mode !== "review" && (
                  <div className="sticky top-0 z-[889] bg-white">
                    <ProgressBar
                      containerClass="p-6 py-3 md:py-6 lg:px-[109px] bg-white"
                      percentage={progressPercentage}
                    />
                    <div
                      className={`px-3 md:px-6 lg:px-[109px] ${
                        Number(
                          (values as OnlineOrderValuesModel).testTypeId
                        ) === 2
                          ? "pb-3"
                          : ""
                      }`}
                    >
                      <SccIntegratedTestBanner
                        values={values}
                        setFieldValue={setFieldValue}
                      />
                    </div>
                  </div>
                )}
                <div id="step6" className="py-0 md:py-0 md:py-10">
                  {steps
                    .filter((step) =>
                      ["Select Test", "Patient Information"].includes(
                        step?.fieldContainerHeading ?? ""
                      )
                    )
                    .map((step) => (
                      <Fragment key={step.stepTitle}>
                        <Section
                          {...{
                            step: {
                              ...step,
                              sectionClass: `${globalSectionClassName} ${step.sectionClass
                                } ${mode === "review" ? "scroll-my-2" : ""}`,
                            },
                            errors,
                            touched,
                            getFieldMeta,
                            getFieldProps,
                            groupFields,
                            setFieldError,
                            setFieldValue,
                            setFieldTouched,
                            validateForm,
                            mode,
                            values,
                            isUpdate,
                            fieldsContainerClass,
                          }}
                        />
                        {step.subSteps && step.subSteps.length > 0 && (
                          <Fragment>
                            {step.subSteps.map((subStep, index) => (
                              <Section
                                key={`${subStep.stepTitle}${index + 1}`}
                                {...{
                                  step: {
                                    ...subStep,
                                    sectionClass: `${globalSectionClassName} ${subStep.sectionClass}`,
                                  },
                                  errors,
                                  touched,
                                  getFieldMeta,
                                  getFieldProps,
                                  setFieldValue,
                                  setFieldTouched,
                                  validateForm,
                                  groupFields,
                                  mode,
                                  values,
                                  fieldsContainerClass,
                                }}
                              />
                            ))}
                          </Fragment>
                        )}
                      </Fragment>
                    ))}
                </div>
                {steps
                  .filter(
                    (step) =>
                      step.fieldContainerHeading !== "Patient Information" &&
                      step.fieldContainerHeading !== "Select Test"
                  )
                  .map((step) => (
                    <Fragment key={step.stepTitle}>
                      <Section
                        {...{
                          step: {
                            ...step,
                            sectionClass: `${globalSectionClassName} ${step.sectionClass
                              } ${mode === "review" ? "scroll-my-2" : ""}`,
                          },
                          errors,
                          touched,
                          getFieldMeta,
                          getFieldProps,
                          groupFields,
                          setFieldError,
                          setFieldValue,
                          setFieldTouched,
                          validateForm,
                          mode,
                          values,
                          fieldsContainerClass,
                          isUpdate,
                        }}
                      />
                      {step.subSteps && step.subSteps.length > 0 && (
                        <Fragment>
                          {step.subSteps.map((subStep, index) => (
                            <Section
                              key={`${subStep.stepTitle}${index + 1}`}
                              {...{
                                step: {
                                  ...subStep,
                                  sectionClass: `${globalSectionClassName} ${subStep.sectionClass}`,
                                },
                                errors,
                                touched,
                                getFieldMeta,
                                getFieldProps,
                                setFieldValue,
                                setFieldTouched,
                                validateForm,
                                groupFields,
                                mode,
                                values,
                                fieldsContainerClass,
                              }}
                            />
                          ))}
                        </Fragment>
                      )}
                    </Fragment>
                  ))}
              </div>
            </div>
          </AdminContentLayout>
        );
      }}
    </Formik>
  );
}

export default forwardRef(MultiStepFrom);
